import React from 'react';
import PropTypes from 'prop-types';
import Box from 'common/components/Box';
import Heading from 'common/components/Heading';
import Container from 'common/components/UI/Container';

import SectionHeader, { QuoteGrid, QuoteCard } from './appClassic.style';

// Quotes sourced from Trustpilot reviews, the former "What our clients say"
// carousel (Widanka, BrentoBox, Wołoszyn; no public source URL) (registry:
// AIBiznesLab/projekty/CloudPouch-Marketing/trustpilot_reviews.jsonl) and the
// Shoutout wall. Long reviews are trimmed (marked with …).
// Cards with sourceUrl: null hold reviews submitted to Trustpilot (verified
// via their notification emails) that Trustpilot never made publicly
// visible — this page is the only place they are published.
// Note: no star ratings or Trustpilot branding here — Trustpilot's free
// business plan does not permit displaying TrustScore/stars or their
// marketing assets. Quotes are the authors' own words (testimonials);
// plain links to public review URLs are fine.
const QUOTES = [
  {
    quote:
      'CloudPouch just reads your local AWS profiles, pulls costs straight to your machine, and shows you where the money goes. I spotted AWS Config rules that had quietly started costing way more than usual… Nobody opens Cost Explorer until the bill already hurts. CloudPouch makes it the kind of thing you actually check on a Tuesday morning with your coffee, not something you panic about at the end of the month.',
    name: 'Anton Babenko',
    role: 'AWS Hero, creator of terraform-aws-modules',
    sourceLabel: 'Read the full review',
    sourceUrl: 'https://www.trustpilot.com/reviews/699c205bf6d1dfdf24b7dc6f',
  },
  {
    quote:
      'Thanks to the CloudPouch application, monitoring AWS cloud costs has become part of my daily to-do list. Regular usage has allowed me to notice recurring patterns in generated costs and pinpoint problematic configurations. Ultimately, this has resulted in a reduction of AWS costs in the project by more than 50% on a monthly scale, and there are still visible anomalies, the removal of which will save even more.',
    name: 'Michał Widanka',
    role: 'AWS Cloud Engineer',
    sourceLabel: null,
    sourceUrl: null,
  },
  {
    quote:
      'Setup was quick, it reads your existing AWS CLI profiles and SSO config so there is nothing to configure. No agents in your account, no billing data leaving your machine. I work with client accounts and I cannot just plug random SaaS tools into their infrastructure… You can tell it was built by someone who actually does this work every day, and that makes all the difference.',
    name: 'Monica Colangelo',
    role: 'AWS Hero',
    sourceLabel: 'Read the full review',
    sourceUrl: 'https://www.trustpilot.com/reviews/69a1c9c9898c8f069ad32ac0',
  },
  {
    quote:
      "If you're working with AWS and want actual visibility into where your cloud budget is going, you need to check out CloudPouch. I've been following it for a while and absolutely love the privacy-first approach: no SaaS, no data leaves your machine, just your AWS profiles, analyzed locally. Perfect for devs, architects, and SREs who are tired of guessing what's driving the bill.",
    name: 'Sena Yakut',
    role: 'AWS Security Hero, Cloud Security Architect',
    sourceLabel: null,
    sourceUrl: null,
  },
  {
    quote:
      'I like it! I think there are often some straightforward configuration wins for DDB tables that can save money without making code changes. Nice work and nice video!',
    name: 'Alex DeBrie',
    role: 'Author of “The DynamoDB Book”',
    sourceLabel: 'via Twitter',
    sourceUrl: 'https://twitter.com/alexbdebrie/status/1507059699514028032',
  },
  {
    quote:
      "People don't realize how useful this tool is until they try to figure out pricing on AWS the first time. Nice work!",
    name: 'BrentoBox',
    role: 'Product Designer & Indie developer',
    sourceLabel: null,
    sourceUrl: null,
  },
  {
    quote:
      "Most AWS cost tools either overwhelm you with dashboards or don't go deep enough. CloudPouch hits the right balance… There's no filler — every feature exists because a real client had a real problem. I spotted cost patterns I'd been missing for months within the first week of use.",
    name: 'Dominik Grzywaczewski',
    role: 'AWS Hero',
    sourceLabel: null,
    sourceUrl: null,
  },
  {
    quote:
      "CloudPouch hands down is the best way for me to navigate my AWS costs across multiple accounts in an intuitive way. There's many solutions out there including things provided by AWS, but this was the first one I used that was straightforward from the start.",
    name: 'Danielle Heberling',
    role: 'AWS Hero',
    sourceLabel: 'Read the full review',
    sourceUrl: 'https://www.trustpilot.com/reviews/6999fd9b93b70fd9829204fd',
  },
  {
    quote:
      'Very useful view of cost across multiple AWS accounts. Easy to navigate, zoom in and out, as well get priority view on cloud spending. It saved me hundreds of dollars and probably more thanks to early detection of leaks.',
    name: 'Guy Ernest',
    role: 'AWS Hero',
    sourceLabel: 'Read the full review',
    sourceUrl: 'https://www.trustpilot.com/reviews/6999fd815bf084cae36e4b4c',
  },
  {
    quote:
      "Great app — helps me to manage my personal projects on AWS. I like simple and clear UI, ability to view multiple AWS accounts and caching of cost explorer data. That makes it easy to quickly understand what's happening.",
    name: 'Rustem Feyzkhanov',
    role: 'AWS Hero',
    sourceLabel: 'Read the full review',
    sourceUrl: 'https://www.trustpilot.com/reviews/699d3c5e7b7db2fe4d0af606',
  },
  {
    quote:
      "CloudPouch is a nice application for getting an high level overview of your AWS expenses as well as diving deep into individual usage types. It integrates well into AWS SSO, so you don't have to create yet another IAM access key. The app has charts for showing you cost over time, so you can pinpoint on when a specific spike happened, or if it's been an ongoing trend.",
    name: 'Sathyajith Bhat',
    role: 'AWS Hero',
    sourceLabel: 'Read the full review',
    sourceUrl: 'https://www.trustpilot.com/reviews/699bd62c4ff1ab8ce684d063',
  },
  {
    quote:
      "I've had a positive experience with Cloudpouch. The platform feels clean and easy to navigate, making cloud data management faster and more efficient. The user interface is intuitive, helping reduce the learning curve when handling complex tasks… I would recommend Cloudpouch to other professionals.",
    name: 'Rossana Suarez',
    role: 'AWS Hero',
    sourceLabel: null,
    sourceUrl: null,
  },
  {
    quote:
      'Cloud-pouch is best cost optimiser tools ever seen, lots of features was there since beginning which now introduce by few cloud provider. Kudos to cloud pouch developer.',
    name: 'Rajkumar Jain',
    role: 'AWS Community Builder',
    sourceLabel: 'Read the full review',
    sourceUrl: 'https://www.trustpilot.com/reviews/69a1a32840a629516971f13a',
  },
  {
    quote:
      'Clear overview of AWS costs. But the real gem are Cost Insights 💎 Here it recommends changing Provisioned Capacity to On-Demand for unused tables in the development environment 🎯',
    name: 'Maciej Radzikowski',
    role: '@radzikowski_m',
    sourceLabel: 'via Twitter',
    sourceUrl: 'https://twitter.com/radzikowski_m/status/1504163599320641536',
  },
  {
    quote:
      "Super cool idea of Paweł! For sure worth to try it and it's for sure better than AWS Budget calculator!",
    name: 'Rafał Wołoszyn',
    role: 'Software Engineer | Founder | CTO',
    sourceLabel: null,
    sourceUrl: null,
  },
];

const ShoutoutTestimonials = ({ sectionWrapper }) => {
  return (
    <Box {...sectionWrapper} as="section" id="testimonial_section">
      <Container>
        <SectionHeader>
          <Heading as="p" className="eyebrow" content="Trusted by AWS Heroes" />
          <Heading content="Endorsed by the AWS community" />
        </SectionHeader>
        <QuoteGrid>
          {QUOTES.map((item) => (
            <QuoteCard key={item.name}>
              <blockquote>{item.quote}</blockquote>
              <footer>
                <span className="name">{item.name}</span>
                <span className="role">{item.role}</span>
                {item.sourceUrl && (
                  <a
                    href={item.sourceUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {item.sourceLabel}
                  </a>
                )}
              </footer>
            </QuoteCard>
          ))}
        </QuoteGrid>
      </Container>
    </Box>
  );
};

ShoutoutTestimonials.propTypes = {
  sectionWrapper: PropTypes.object,
};

ShoutoutTestimonials.defaultProps = {
  sectionWrapper: {
    pt: ['60px', '80px', '90px', '100px', '100px'],
    pb: ['10px', '10px', '10px', '10px', '10px'],
  },
};

export default ShoutoutTestimonials;
